import dayjs, { type Dayjs } from 'dayjs';
import { RRule, RRuleSet, Frequency, rrulestr, type Weekday, type WeekdayStr } from 'rrule';
import type { Options, ParsedOptions } from 'rrule/dist/esm/types';
import { DateTime } from 'lib/utils/date-handling/DateTime';

const RRULE_BY_WEEKDAY: Record<WeekdayStr, Weekday> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
};

/** dayjs `.day()` index → WeekdayStr (0 = Sunday). */
const WEEKDAY_STR_BY_DAYJS_DAY: WeekdayStr[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const DAYJS_DAY_BY_WEEKDAY_STR: Record<WeekdayStr, number> = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
};

/** Map form `WeekdayStr` values to rrule `Weekday` instances (e.g. `RRule.MO`). */
export const toRruleWeekdays = (weekdays: WeekdayStr[]): Weekday[] => weekdays.map(day => RRULE_BY_WEEKDAY[day]);

/**
 * Build an RRuleSet from options or an RRULE string. `new RRule` / `RRule.fromString` only
 * understand RRULE/EXRULE/DTSTART and throw on any other component (e.g. `Unsupported RFC prop
 * EXDATE`). `rrulestr(..., { forceset: true })` returns an RRuleSet that accepts those extra lines.
 */
export function toRRuleSet(rruleStringOrOptions: string | Partial<Options>): RRuleSet {
  const rruleString =
    typeof rruleStringOrOptions === 'string' ? rruleStringOrOptions : RRule.optionsToString(rruleStringOrOptions);

  return rrulestr(rruleString, { forceset: true }) as RRuleSet;
}

/**
 * Parse an RRULE string into its options, tolerating EXDATE/RDATE lines.
 */
export const parseRRuleString = (rruleString: string): ParsedOptions => toRRuleSet(rruleString).rrules()[0].options;

/**
 * Map local form weekdays to rrule weekdays in UTC for the given local datetime.
 * Needed when DTSTART is stored as UTC (`Z`) so BYDAY matches the UTC calendar day.
 */
export const toRruleWeekdaysUtc = (localWeekdays: WeekdayStr[], localDateTime: Dayjs): Weekday[] => {
  const dayShift = localDateTime.utc().day() - localDateTime.day();

  return localWeekdays.map(day => {
    const shiftedDayjsDay = (DAYJS_DAY_BY_WEEKDAY_STR[day] + dayShift + 7) % 7;

    return RRULE_BY_WEEKDAY[WEEKDAY_STR_BY_DAYJS_DAY[shiftedDayjsDay]];
  });
};

/**
 * Inverse of `toRruleWeekdaysUtc`: map UTC BYDAY weekdays back to local form weekdays
 * for the given local datetime (e.g. when hydrating the edit form from an rrule).
 */
export const fromRruleWeekdaysUtc = (utcWeekdays: WeekdayStr[], localDateTime: Dayjs): WeekdayStr[] => {
  const dayShift = localDateTime.utc().day() - localDateTime.day();

  return utcWeekdays.map(day => {
    const shiftedDayjsDay = (DAYJS_DAY_BY_WEEKDAY_STR[day] - dayShift + 7) % 7;

    return WEEKDAY_STR_BY_DAYJS_DAY[shiftedDayjsDay];
  });
};

export const toOneTimeRRuleSet = ({ dtstart }: { dtstart: Date }): RRuleSet =>
  toRRuleSet({
    freq: Frequency.DAILY,
    interval: 1,
    count: 1,
    dtstart,
    tzid: DateTime.getBrowserTimeZone(),
  });

export const toCV50UntilDate = ({ until }: { until: Date }): Date =>
  dayjs.utc(until).hour(23).minute(59).second(0).millisecond(0).toDate();
